import { useSnackbar } from "notistack";
import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  InputBase,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";
import { CreateGroup } from "../../hooks/useUserGroupdata";
import { GetFacilitiesByOrganisationId } from "../../hooks/useFacilityData";

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";

const SectionLabel = ({ index, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "11px",
        fontWeight: 600,
        color: BRAND,
        letterSpacing: "0.1em",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {index}
    </Typography>
    <Box sx={{ width: 14, height: "1px", background: LINE }} />
    <Typography
      sx={{
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: "0.24em",
        textTransform: "uppercase",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      {children}
    </Typography>
  </Box>
);

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: INK_MUTED,
      mb: 1,
      fontFamily: '"DM Sans", sans-serif',
    }}
  >
    {children}
  </Typography>
);

const TextInput = ({ value, onChange, placeholder, autoFocus }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: 2,
      height: 48,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "12px",
      transition: "all 0.25s ease",
      "&:hover": { borderColor: "rgba(10,22,40,0.2)" },
      "&:focus-within": {
        borderColor: "rgba(10,22,40,0.32)",
        boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
      },
    }}
  >
    <InputBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoFocus={autoFocus}
      fullWidth
      sx={{
        fontSize: "15px",
        fontWeight: 500,
        fontFamily: '"DM Sans", sans-serif',
        color: INK,
        "& input::placeholder": { color: INK_MUTED, opacity: 1 },
      }}
    />
  </Box>
);

const FacilitySelect = ({ value, onChange, facilities, loading }) => (
  <FormControl fullWidth>
    <Select
      value={value}
      onChange={onChange}
      displayEmpty
      disabled={loading}
      IconComponent={KeyboardArrowDownRoundedIcon}
      MenuProps={{
        PaperProps: {
          sx: {
            mt: 1,
            borderRadius: "12px",
            border: `1px solid ${LINE}`,
            boxShadow: "0 24px 48px -24px rgba(10,22,40,0.18)",
            "& .MuiMenuItem-root": {
              fontSize: "14px",
              fontFamily: '"DM Sans", sans-serif',
              py: 1.25,
              px: 2,
              "&.Mui-selected": {
                background: "rgba(10,22,40,0.04)",
                "&:hover": { background: "rgba(10,22,40,0.06)" },
              },
            },
          },
        },
      }}
      sx={{
        height: 48,
        background: "#fff",
        fontSize: "15px",
        fontWeight: 500,
        fontFamily: '"DM Sans", sans-serif',
        color: INK,
        "& .MuiSelect-select": {
          py: 0,
          display: "flex",
          alignItems: "center",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: LINE,
          borderRadius: "12px",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(10,22,40,0.2)",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(10,22,40,0.32)",
          borderWidth: "1px",
        },
        "& .MuiSelect-icon": { color: INK_MUTED },
      }}
    >
      <MenuItem value="">
        <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
          Ingen anläggning
        </Box>
      </MenuItem>
      {facilities.map((facility) => (
        <MenuItem key={facility.id} value={facility.id}>
          {facility.facility_name}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const PermissionToggle = ({ icon: Icon, title, description, active, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2.25,
      p: 2.25,
      width: "100%",
      textAlign: "left",
      cursor: "pointer",
      background: active ? "rgba(138,17,37,0.025)" : "#fff",
      border: `1px solid ${active ? "rgba(138,17,37,0.28)" : LINE}`,
      borderRadius: "14px",
      position: "relative",
      overflow: "hidden",
      transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
      fontFamily: '"DM Sans", sans-serif',
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        background: BRAND,
        transform: active ? "scaleY(1)" : "scaleY(0)",
        transformOrigin: "center",
        transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        borderColor: active ? "rgba(138,17,37,0.4)" : "rgba(10,22,40,0.2)",
      },
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        minWidth: 44,
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: active
          ? `linear-gradient(135deg, ${BRAND}, #c23b54)`
          : "#f4f2ee",
        color: active ? "#fff" : INK_MUTED,
        transition: "all 0.35s ease",
        boxShadow: active ? "0 10px 22px -10px rgba(138,17,37,0.5)" : "none",
      }}
    >
      <Icon sx={{ fontSize: 20 }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: "16px",
          color: INK,
          letterSpacing: "-0.005em",
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: "12.5px",
          color: INK_MUTED,
          mt: 0.35,
          fontFamily: '"DM Sans", sans-serif',
          lineHeight: 1.4,
        }}
      >
        {description}
      </Typography>
    </Box>
    <Box
      sx={{
        width: 42,
        height: 24,
        borderRadius: "999px",
        background: active ? BRAND : "rgba(10,22,40,0.14)",
        position: "relative",
        transition: "background 0.3s ease",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 2,
          left: 2,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 2px 6px rgba(10,22,40,0.3)",
          transform: active ? "translateX(18px)" : "translateX(0)",
          transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
        }}
      />
    </Box>
  </Box>
);

export const CreateGroupModal = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [groupName, setGroupName] = useState("");
  const [canAlarm, setCanAlarm] = useState(false);
  const [canChat, setCanChat] = useState(false);
  const [facilityId, setFacilityId] = useState("");

  const organisationId = props.customerid;

  const { data: facilitiesData, isLoading: facilitiesLoading } =
    GetFacilitiesByOrganisationId(organisationId);
  const facilities = facilitiesData?.data || [];

  const createGroupMutation = CreateGroup({
    onSuccess: () => {
      enqueueSnackbar("Grupp skapad.", { variant: "success" });
      props.closeCreateModal();
    },
    onError: () => {
      enqueueSnackbar("Kunde inte skapa grupp.", { variant: "error" });
    },
  });

  const handleCreate = () => {
    createGroupMutation.mutate({
      organisation_id: parseInt(organisationId),
      group_name: groupName.trim(),
      can_alarm: canAlarm,
      can_chat: canChat,
      facility_id: facilityId ? parseInt(facilityId) : null,
    });
  };

  const saving = createGroupMutation.isLoading;
  const canSave = groupName.trim().length > 0 && !saving;

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <SectionLabel index="01">Identitet</SectionLabel>
        <Stack spacing={2.25}>
          <Box>
            <FieldLabel>Gruppnamn</FieldLabel>
            <TextInput
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="T.ex. Administratörer"
              autoFocus
            />
          </Box>
          <Box>
            <FieldLabel>Tillhör anläggning</FieldLabel>
            <FacilitySelect
              value={facilityId}
              onChange={(e) => setFacilityId(e.target.value)}
              facilities={facilities}
              loading={facilitiesLoading}
            />
          </Box>
        </Stack>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionLabel index="02">Behörigheter</SectionLabel>
        <Stack spacing={1.5}>
          <PermissionToggle
            icon={NotificationsActiveRoundedIcon}
            title="Kan larma"
            description="Tillåt gruppens medlemmar att trigga och hantera larm"
            active={canAlarm}
            onClick={() => setCanAlarm((v) => !v)}
          />
          <PermissionToggle
            icon={ChatBubbleRoundedIcon}
            title="Kan chatta"
            description="Tillåt gruppens medlemmar att använda chatfunktioner"
            active={canChat}
            onClick={() => setCanChat((v) => !v)}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.25,
          pt: 3,
          borderTop: `1px solid ${LINE_SOFT}`,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => props.closeCreateModal()}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2.25,
            py: 1.25,
            background: "transparent",
            color: INK_MUTED,
            border: `1px solid ${LINE}`,
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.25s ease",
            "&:hover": {
              color: INK,
              borderColor: "rgba(10,22,40,0.22)",
            },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
          Avbryt
        </Box>
        <Box
          component="button"
          type="button"
          onClick={handleCreate}
          disabled={!canSave}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2.75,
            py: 1.25,
            background: INK,
            color: "#fff",
            border: "none",
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: canSave ? "pointer" : "not-allowed",
            opacity: canSave ? 1 : 0.4,
            transition: "all 0.25s ease",
            boxShadow: canSave ? "0 10px 24px -12px rgba(10,22,40,0.55)" : "none",
            "&:hover": canSave
              ? { background: "#1a2638", transform: "translateY(-1px)" }
              : {},
          }}
        >
          {saving ? (
            <CircularProgress size={14} sx={{ color: "#fff" }} />
          ) : (
            <CheckRoundedIcon sx={{ fontSize: 16 }} />
          )}
          Skapa grupp
        </Box>
      </Box>
    </Box>
  );
};
